import {CartService} from './CartService';
import {StyleSettingsService} from './StyleSettingsService';
import {PickupMethod, SiteStore} from '@wix/wixstores-client-storefront-sdk';
import {DeliverySectionModel} from '../models/DeliverySection.model';
import {SPECS} from '../specs';
import {ViewerContext} from '@wix/ecom-platform-storefront-common';
import {EcommerceSettingsService} from './EcommerceSettingsService';

export class OrderService {
  private readonly cartService: CartService;
  private readonly styleSettingsService: StyleSettingsService;
  private readonly siteStore: SiteStore;
  private readonly viewerContext: ViewerContext;
  private readonly ecommerceSettingsService: EcommerceSettingsService;
  public destinationWasChanged: boolean = false;

  constructor({
    cartService,
    ecommerceSettingsService,
    styleSettingsService,
    siteStore,
    viewerContext,
  }: {
    cartService: CartService;
    ecommerceSettingsService: EcommerceSettingsService;
    styleSettingsService: StyleSettingsService;
    siteStore: SiteStore;
    viewerContext: ViewerContext;
  }) {
    this.cartService = cartService;
    this.styleSettingsService = styleSettingsService;
    this.siteStore = siteStore;
    this.viewerContext = viewerContext;
    this.ecommerceSettingsService = ecommerceSettingsService;
  }

  public get canShipToDestination() {
    return this.deliverySectionModel?.canShipToDestination;
  }

  public get hasShippingRules(): boolean {
    return !!this.deliverySectionModel?.options?.length;
  }

  public get canShip() {
    /* istanbul ignore next */
    return (
      this.hasShippingRules ||
      //todo(guy segev): test delivery error for digital (cart.errors.cant_sell_to_country?)
      /* istanbul ignore next */
      this.cartService.isNonShippableCart ||
      /* istanbul ignore next */
      !this.destinationWasChanged
    );
  }

  public isPickup(): boolean {
    return this.cartService.cartSummary?.deliverySummary?.method?.pickup;
  }

  public hasTimeSlots(): boolean {
    return this.deliverySectionModel?.hasTimeSlots;
  }

  public get isPickupPoint(): boolean {
    /* istanbul ignore next */
    if (this.viewerContext.isExperimentEnabled(SPECS.RemovePickupPointSpecificTitleOnCart)) {
      return undefined;
    }
    return this.cartService.cartSummary?.deliverySummary?.method?.pickupMethod === PickupMethod.PICKUP_POINT;
  }

  public get deliverySectionModel(): DeliverySectionModel {
    return this.cartService.deliverySectionModel;
  }

  public get hasPickupOption(): boolean {
    return this.deliverySectionModel?.options.some((option) => option.isPickup);
  }

  public get hasEstimatedDestination(): boolean {
    return !!this.cartService.cartModel?.deliveryInfo?.address.country;
  }

  public get isEstimatedDestinationIsShippable() {
    return (
      this.hasEstimatedDestination &&
      (this.hasShippingRules || this.cartService.isNonShippableCart || this.destinationWasChanged)
    );
  }

  public get taxOnProduct() {
    const moveCartToViewerContext = this.viewerContext.isExperimentEnabled(SPECS.MoveCartToViewerContext);
    return moveCartToViewerContext
      ? this.ecommerceSettingsService.getEcommerceSettings().tax.taxIncludedInItemPrices ??
          /* istanbul ignore next */ false
      : this.siteStore.priceSettings?.taxOnProduct ?? /* istanbul ignore next */ false;
  }

  public get shouldDisplayTax() {
    const {shouldShowTax} = this.styleSettingsService;

    return (
      shouldShowTax &&
      !this.taxOnProduct &&
      (!this.cartService.isNonShippableCart || this.isEstimatedDestinationIsShippable)
    );
  }

  public get minimumOrderAmount() {
    return this.cartService.cartSummary?.minimumOrderAmount;
  }

  public get hasShippingDestination() {
    return this.hasEstimatedDestination && (this.canShipToDestination || this.destinationWasChanged);
  }
}
